import type { GridDragTarget } from './dragging';

export const GRID_DRAG_HOVER_GLOW_COLOR = 'rgba(255, 193, 7, 0.95)';
export const GRID_DRAG_HOVER_POINT_RADIUS = 9;
export const GRID_DRAG_MOVE_MODE_GLOW_COLOR = 'rgba(255, 193, 7, 0.42)';

export interface GridDragHoverPoint {
  x: number;
  y: number;
}

export interface GridDragHoverGraphPoint {
  cx: number;
  cy: number;
}

export interface GridDragHoverObject {
  t?: string;
  cx?: number;
  cy?: number;
  high?: boolean;
  first?: GridDragHoverGraphPoint;
  second?: GridDragHoverGraphPoint;
  path?: number[][] | null;
  sideways?: boolean;
  lw?: number;
  [key: string]: unknown;
}

type GridDragHoverStandalonePoint = GridDragHoverObject &
  GridDragHoverGraphPoint & { t: 'dot' };

export interface GridDragHoverContext {
  save: () => void;
  restore: () => void;
  beginPath: () => void;
  arc: (
    x: number,
    y: number,
    radius: number,
    startAngle: number,
    endAngle: number
  ) => void;
  moveTo: (x: number, y: number) => void;
  lineTo: (x: number, y: number) => void;
  stroke: () => void;
  rotate: (angle: number) => void;
  strokeStyle: string;
  shadowColor: string;
  shadowBlur: number;
  lineCap: string;
  lineJoin: string;
  lineWidth: number;
}

const graphPointToHoverPoint = (
  point: GridDragHoverGraphPoint
): GridDragHoverPoint => ({
  x: point.cx,
  y: point.cy,
});

const isStandalonePoint = (
  obj: GridDragHoverObject | undefined
): obj is GridDragHoverStandalonePoint =>
  Boolean(
    obj &&
      obj.t === 'dot' &&
      !obj.high &&
      typeof obj.cx === 'number' &&
      typeof obj.cy === 'number'
  );

const getHoveredPointForObject = (
  obj: GridDragHoverObject,
  hoveredPoint: GridDragHoverGraphPoint | undefined
): GridDragHoverGraphPoint | undefined => {
  if (!hoveredPoint) {
    return undefined;
  }

  if (isStandalonePoint(obj) && obj === hoveredPoint) {
    return hoveredPoint;
  }

  return obj.first === hoveredPoint || obj.second === hoveredPoint
    ? hoveredPoint
    : undefined;
};

const isHoveredDragTarget = ({
  obj,
  hoveredObject,
  hoveredPoint,
}: {
  obj: GridDragHoverObject | undefined;
  hoveredObject: GridDragHoverObject | undefined;
  hoveredPoint?: GridDragHoverGraphPoint;
}): boolean =>
  Boolean(
    obj &&
      (hoveredObject === obj ||
        (hoveredPoint && getHoveredPointForObject(obj, hoveredPoint)))
  );

const getRayGlowEndpoint = (
  obj: GridDragHoverObject,
  linePoints: GridDragHoverPoint[]
): GridDragHoverPoint | undefined => {
  if (!obj.first || !obj.second || linePoints.length < 2) {
    return undefined;
  }

  if (obj.second.cx === obj.first.cx) {
    return {
      x: obj.second.cx,
      y: obj.second.cy > obj.first.cy ? linePoints[1].y : linePoints[0].y,
    };
  }

  if (obj.second.cy === obj.first.cy) {
    return {
      x: obj.second.cx > obj.first.cx ? linePoints[1].x : linePoints[0].x,
      y: obj.second.cy,
    };
  }

  return obj.second.cx > obj.first.cx ? linePoints[1] : linePoints[0];
};

export const getGridDragHoverGlowPoints = (
  obj: GridDragHoverObject | undefined,
  linePoints?: GridDragHoverPoint[]
): GridDragHoverPoint[] | undefined => {
  if (!obj) {
    return undefined;
  }

  if (obj.t === 'Parabola' && obj.path && obj.path.length > 0) {
    return obj.path.map(([x, y]) => ({ x, y }));
  }

  if (obj.t === 'Segment' && obj.first && obj.second) {
    return [
      graphPointToHoverPoint(obj.first),
      graphPointToHoverPoint(obj.second),
    ];
  }

  if (obj.t === 'Line' && obj.second && linePoints && linePoints.length >= 2) {
    return [linePoints[0], linePoints[1]];
  }

  if (obj.t === 'Ray' && obj.first && obj.second && linePoints) {
    const endpoint = getRayGlowEndpoint(obj, linePoints);

    if (endpoint) {
      return [graphPointToHoverPoint(obj.first), endpoint];
    }
  }

  return undefined;
};

export const paintGridDragHoverGlow = ({
  ctx,
  obj,
  hoveredObject,
  hoveredPoint,
  linePoints,
  color = GRID_DRAG_HOVER_GLOW_COLOR,
}: {
  ctx: GridDragHoverContext;
  obj: GridDragHoverObject | undefined;
  hoveredObject: GridDragHoverObject | undefined;
  hoveredPoint?: GridDragHoverGraphPoint;
  linePoints?: GridDragHoverPoint[];
  color?: string;
}): boolean => {
  if (!obj) {
    return false;
  }

  const pointGlow = getHoveredPointForObject(obj, hoveredPoint);

  const glowPoints = getGridDragHoverGlowPoints(obj, linePoints);

  if (
    !pointGlow &&
    (hoveredObject !== obj || !glowPoints || glowPoints.length === 0)
  ) {
    return false;
  }

  ctx.save();
  ctx.strokeStyle = color;
  ctx.shadowColor = color;
  ctx.shadowBlur = 10;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (obj.sideways) {
    ctx.rotate(Math.PI / 2);
  }

  ctx.beginPath();

  if (pointGlow) {
    ctx.lineWidth = 6;
    ctx.arc(
      pointGlow.cx,
      pointGlow.cy,
      GRID_DRAG_HOVER_POINT_RADIUS,
      0,
      Math.PI * 2
    );
  } else if (glowPoints && glowPoints.length > 0) {
    ctx.lineWidth = (obj.lw || 3) + 8;
    ctx.moveTo(glowPoints[0].x, glowPoints[0].y);

    for (let index = 1; index < glowPoints.length; index++) {
      ctx.lineTo(glowPoints[index].x, glowPoints[index].y);
    }
  }

  ctx.stroke();
  ctx.restore();

  return true;
};

const paintGridDragMoveModeGlow = ({
  ctx,
  obj,
  linePoints,
}: {
  ctx: GridDragHoverContext;
  obj: GridDragHoverObject | undefined;
  linePoints?: GridDragHoverPoint[];
}): boolean =>
  paintGridDragHoverGlow({
    ctx,
    obj,
    hoveredObject: obj,
    hoveredPoint: isStandalonePoint(obj) ? obj : undefined,
    linePoints,
    color: GRID_DRAG_MOVE_MODE_GLOW_COLOR,
  });

export const paintGridDragModeGlow = ({
  moveModeActive,
  ctx,
  obj,
  hoveredObject,
  hoveredPoint,
  activeDragTarget,
  linePoints,
}: {
  moveModeActive: boolean;
  ctx: GridDragHoverContext;
  obj: GridDragHoverObject | undefined;
  hoveredObject: GridDragHoverObject | undefined;
  hoveredPoint?: GridDragHoverGraphPoint;
  activeDragTarget?: GridDragTarget;
  linePoints?: GridDragHoverPoint[];
}): boolean => {
  if (!moveModeActive) {
    return false;
  }

  const activeHoveredObject =
    activeDragTarget?.kind === 'object' ? activeDragTarget.obj : undefined;
  const activeHoveredPoint =
    activeDragTarget?.kind === 'point' ? activeDragTarget.point : undefined;
  const resolvedHoveredObject = activeDragTarget
    ? activeHoveredObject
    : hoveredObject;
  const resolvedHoveredPoint = activeDragTarget
    ? activeHoveredPoint
    : hoveredPoint;

  const hasHoveredTarget = Boolean(
    resolvedHoveredObject || resolvedHoveredPoint
  );
  const isHoveredTarget = isHoveredDragTarget({
    obj,
    hoveredObject: resolvedHoveredObject,
    hoveredPoint: resolvedHoveredPoint,
  });
  const paintedMoveModeGlow =
    !hasHoveredTarget || isHoveredTarget
      ? paintGridDragMoveModeGlow({
          ctx,
          obj,
          linePoints,
        })
      : false;

  const paintedHoverGlow =
    hasHoveredTarget && isHoveredTarget
      ? paintGridDragHoverGlow({
          ctx,
          obj,
          hoveredObject: resolvedHoveredObject,
          hoveredPoint: resolvedHoveredPoint,
          linePoints,
        })
      : false;

  return paintedMoveModeGlow || paintedHoverGlow;
};
